import React, { useMemo, useCallback } from 'react';
import type { PurchaseInfo } from '../../../types/process/purchase';
import { PurchaseTabScene } from '../../../types/process/purchase';
import type { FieldConfig } from '../../../components/ForgeQueryBuilder/types';
import purchaseApi from '../../../services/process/purchase';
import PurchaseSceneTable from './PurchaseSceneTable';
import TabListTemplate from '../../../components/Templates/TabList';
import type { TabListViewMode, TabListStatItem } from '../../../components/Templates/TabList/types';
import { getDefaultQuery } from '../../../pages/purchase/purchase.effect';
import type { ForgeQuery } from '../../../components/ForgeQueryBuilder';

export interface PurchaseListActionContext {
  scene?: string;
  sceneStat?: TabListStatItem;
}

import { ShowField } from './PurchaseSceneTable';

export interface ViewModeConfig {
  mode: 'table' | 'list';
  label: string;
  renderActions?: (order: PurchaseInfo, context?: PurchaseListActionContext) => React.ReactNode;
  renderInfo?: (order: PurchaseInfo, context?: PurchaseListActionContext) => React.ReactNode;
  showField?: ShowField;
  renderBatchActions?: (
    selectedRows: PurchaseInfo[],
    clearSelection: () => void,
    activeTabKey?: string
  ) => React.ReactNode;
}

export interface PurchaseListTemplateProps {
  /** 查询字段配置 */
  queryFields: FieldConfig[];

  /** Tab 统计场景 */
  tabScene?: PurchaseTabScene;
  defaultScene?: string;
  /** 视图模式配置 */
  viewModes?: ViewModeConfig[];
  /** 标题 */
  title?: string;
  /** 触发刷新 */
  refreshKey?: number;
  /** 导出回调 */
  onExport?: (query: ForgeQuery) => void;
}

const PurchaseListHeader: React.FC = () => (
  <div style={{
    display: 'flex',
    background: '#e6f7ff',
    padding: '12px 0',
    fontWeight: 'bold',
    borderRadius: '4px 4px 0 0',
    marginBottom: 0,
    borderBottom: '1px solid #f0f0f0'
  }}>
    <div style={{ flex: 1, paddingLeft: 16 }}>采购信息</div>
  </div>
);

const PurchaseListTemplate: React.FC<PurchaseListTemplateProps> = ({
  queryFields,

  tabScene,
  defaultScene,
  viewModes = [{ mode: 'table', label: '表格页' }],
  title,
  refreshKey,
  onExport,
}) => {
  // Map old Purchase viewModes to new TabListViewModes
  const tabListViewModes: TabListViewMode<PurchaseInfo>[] = useMemo(() => {
    return viewModes.map(vm => ({
      mode: vm.mode,
      label: vm.label,
      /** 使用采购单号作为行唯一标识，供 TabList 在列表刷新后同步 selectedRows */
      rowKey: (row: PurchaseInfo) => row.purchase.purchaseNo,
      renderBatchActions: vm.renderBatchActions,
      renderList: ({ rows, activeTabKey, activeStat, selectedRowKeys, onChangeSelection }) => {
        const currentActionContext: PurchaseListActionContext = {
          scene: activeTabKey,
          sceneStat: activeStat,
        };

        if (vm.mode === 'table') {
          return (
            <PurchaseSceneTable
              dataSource={rows}
              activeScene={activeTabKey}
              renderActions={
                vm.renderActions
                  ? (order) => vm.renderActions!(order, currentActionContext)
                  : undefined
              }
              showField={vm.showField}
              rowSelection={
                selectedRowKeys
                  ? {
                    selectedRowKeys,
                    onChange: (keys: React.Key[], selectedRows: PurchaseInfo[]) => {
                      onChangeSelection?.(keys, selectedRows);
                    }
                  }
                  : undefined
              }
            />
          );
        }

        // Fallback for list mode
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <PurchaseListHeader />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
              {rows.map((order) => (
                <div key={order.purchase.purchaseNo}>List mode not fully implemented</div>
              ))}
            </div>
          </div>
        );
      }
    }));
  }, [viewModes]);



  const fetchTabStats = useMemo(() => {
    if (!tabScene) return undefined;
    return async (query: ForgeQuery) => purchaseApi.getStats(tabScene, query as any);
  }, [tabScene]);

  const fetchList = useCallback(async (params: any, activeTabKey?: string) => {
    const mergedParams = { ...params };
    if (activeTabKey) {
      mergedParams.scene = activeTabKey;
    }
    const res = await purchaseApi.list(mergedParams);
    return {
      rows: res.data?.items || [],
      pagination: {
        page: res.data?.pagination?.page || 1,
        page_size: res.data?.pagination?.page_size || 20,
        total: res.data?.pagination?.total || 0,
        total_pages: res.data?.pagination?.total_pages || 0,
      }
    };
  }, []);

  const pageListKey = `${tabScene || ''}`;

  return (
    <TabListTemplate<PurchaseInfo>
      title={title}
      queryFields={queryFields}
      defaultTabKey={defaultScene}
      fetchTabStats={fetchTabStats}
      fetchList={fetchList}
      getDefaultQuery={getDefaultQuery}
      viewModes={tabListViewModes}
      refreshKey={refreshKey}
      pageListKey={pageListKey}
      onExport={onExport}
    />
  );
};

export default PurchaseListTemplate;
